interface Options {
  date: boolean;
  hour: boolean;
}

class DateUtils {
  static isAfter = (date: Date, afterDate: Date, equal: boolean): boolean => {
    afterDate.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return equal ? date >= afterDate : date > afterDate;
  }

  static isNowAfter = (date: Date, afterDate: Date): boolean => DateUtils.isAfter(
    date, afterDate, true,
  )

  static formatToUS = (value: string): string => {
    const [day, month, year] = value.split('/');
    return `${year}-${month}-${day}`;
  }

  /**
   * Normalize a date string to YYYY-MM-DD format without timezone conversion
   * This prevents date shifts when dealing with timezone conversions
   * Handles ISO timestamps, YYYY-MM-DD, and other date formats
   * @param dateString - Date string in any format (ISO, YYYY-MM-DD, etc.)
   * @returns Date string in YYYY-MM-DD format using local date components
   */
  static normalizeDateToYYYYMMDD = (dateString: string | null | undefined): string | null => {
    if (!dateString) {
      return null;
    }

    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // If ISO timestamp format (YYYY-MM-DDTHH:MM:SS.sssZ), extract just the date part
    // This avoids timezone conversion issues when parsing with new Date()
    if (/^\d{4}-\d{2}-\d{2}T/.test(dateString)) {
      return dateString.split('T')[0];
    }

    // For other formats, parse the date and use local date components
    const date = new Date(dateString);

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    // Use local date methods to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();

    // Format as YYYY-MM-DD with zero padding
    const yearStr = year.toString();
    const monthStr = month.toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');

    return `${yearStr}-${monthStr}-${dayStr}`;
  }

  format = (date: Date, options: Options): string => {
    let result = '';

    if (options.date) {
      const day = this.padZeros(date.getUTCDate(), 2);
      const month = this.padZeros(date.getUTCMonth() + 1, 2);
      const year = date.getUTCFullYear();
      result += `${day}/${month}/${year}`;
    }

    if (options.date && options.hour) {
      result += ' ';
    }

    if (options.hour) {
      const hours = this.padZeros(date.getUTCHours(), 2);
      const minutes = this.padZeros(date.getUTCMinutes(), 2);
      const seconds = this.padZeros(date.getUTCSeconds(), 2);
      result += `${hours}:${minutes}:${seconds}`;
    }

    return result;
  }

  padZeros = (n: string | number, length: number): string => {
    const str = n.toString();
    const leadingZerosCount = length > str.length ? length - str.length : 0;
    return '0'.repeat(leadingZerosCount) + str;
  }
}

export default DateUtils;
